import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Eye, Target, Trash2, UserPlus, BookOpen, BookMarked, CalendarCheck } from 'lucide-react';

const EvaluationCard = ({ 
  catedraId, 
  evaluacion, 
  onDeleteEvaluation, 
  onAssignEvaluation, 
  showStatus = false, 
  showActions = false, 
  docenteView = false,
  showResultsButton = false,
  onViewResults
}) => {
  const navigate = useNavigate();
  // Función para obtener el estado desde EvaluacionAsignacion
  const getEstado = () => {
    if (evaluacion.EvaluacionAsignacion && evaluacion.EvaluacionAsignacion.length > 0) {
      return evaluacion.EvaluacionAsignacion[0].estado;
    }
    return evaluacion.estado || 'PENDIENTE';
  };

  // Función para obtener el texto del estado
  const getEstadoText = (estado) => {
    switch(estado) {
      case 'PENDIENTE': return 'Pendiente';
      case 'VENCIDA': return 'Vencida';
      case 'REALIZADA': return 'Realizada';
      case 'CALIFICADA': return 'Calificada';
      default: return estado;
    }
  };

  // Función para obtener el color del estado
  const getEstadoColor = (estado) => {
    switch(estado) {
      case 'PENDIENTE': return 'bg-yellow-600/20 text-yellow-300 border-yellow-500/30';
      case 'VENCIDA': return 'bg-red-600/20 text-red-300 border-red-500/30';
      case 'REALIZADA': return 'bg-blue-600/20 text-blue-300 border-blue-500/30';
      case 'CALIFICADA': return 'bg-green-600/20 text-green-300 border-green-500/30';
      default: return 'bg-gray-600/20 text-gray-300 border-gray-500/30';
    }
  };

  const estado = getEstado();

  return (
    <div className="bg-white/5 backdrop-blur-lg p-4 rounded-lg shadow-xl mb-4 border border-slate-700">
      <div className="flex justify-between items-start mb-2">
        <h3 className="text-lg font-bold text-orange-300">{evaluacion.titulo}</h3>
        <div className="flex gap-2">
          <span className="px-2 py-1 text-xs leading-5 font-semibold rounded-full bg-blue-600/20 text-blue-300 border border-blue-500/30">
            <div className="inline-flex items-center gap-1">
              <Target size={14} />
              {evaluacion.Pregunta?.length || 0} Preguntas
            </div>
          </span>
          {showStatus && (
            <span className={`px-2 py-1 text-xs leading-5 font-semibold rounded-full ${getEstadoColor(estado)}`}>
              {getEstadoText(estado)}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col space-y-1 text-sm text-gray-400">
        <div className="flex items-center gap-2">
          <BookOpen size={16} className="text-emerald-400" />
          <span><strong>Cátedra:</strong> {evaluacion.Catedra?.nombre || 'N/A'}</span>
        </div>
        {evaluacion.UnidadPlan?.PlanDeClases?.titulo && (
          <div className="flex items-center gap-2">
            <BookMarked size={16} className="text-blue-400" />
            <span><strong>Plan:</strong> {evaluacion.UnidadPlan.PlanDeClases.titulo}</span>
          </div>
        )}
        {evaluacion.UnidadPlan?.periodo && (
          <div className="flex items-center gap-2">
            <Target size={16} className="text-purple-400" />
            <span><strong>Unidad:</strong> {evaluacion.UnidadPlan.periodo}</span>
          </div>
        )}
        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-700">
          <CalendarCheck size={16} className="text-gray-400" />
          <span><strong>Fecha de Creación:</strong> {format(new Date(evaluacion.created_at), 'dd/MM/yyyy')}</span>
        </div>
      </div>
      
      <div className="mt-4 pt-4 border-t border-slate-700 flex items-center gap-2">
        {/* Botón para realizar evaluación (siempre visible para alumnos pendientes) */}
        {showActions && !docenteView && estado !== 'REALIZADA' && estado !== 'CALIFICADA' && (
          <Link
            to={`/evaluacion/${evaluacion.id}`}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600/20 text-blue-300 hover:bg-blue-600/30 hover:text-blue-200 rounded-lg transition-all duration-200 border border-blue-500/30"
          >
            <span className="text-sm font-medium">Realizar</span>
          </Link>
        )}

        {/* Botón para ver resultados (solo si showResultsButton es true y está REALIZADA o CALIFICADA) */}
        {showResultsButton && (estado === 'REALIZADA' || estado === 'CALIFICADA') && (
          <button
            onClick={() => onViewResults(evaluacion.Catedra?.id, evaluacion.id)}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-600/20 text-emerald-300 hover:bg-emerald-600/30 hover:text-emerald-200 rounded-lg transition-all duration-200 border border-emerald-500/30"
          >
            <Eye size={16} />
            <span className="text-sm font-medium">Ver Resultados</span>
          </button>
        )}

        {/* Botón de eliminar (solo si se pasa la función) */}
        {onDeleteEvaluation && (
          <button
            onClick={() => onDeleteEvaluation(evaluacion.id)}
            className="p-2 bg-red-600/20 text-red-300 hover:bg-red-600/30 hover:text-red-200 rounded-lg transition-all duration-200 border border-red-500/30 ml-auto"
            title="Eliminar Evaluación"
          >
            <Trash2 size={16} />
          </button>
        )}

        {onAssignEvaluation && (
          <button
            onClick={() => onAssignEvaluation(evaluacion)}
            className="p-2 bg-blue-600/20 text-blue-300 hover:bg-blue-600/30 hover:text-blue-200 rounded-lg transition-all duration-200 border border-blue-500/30 ml-2"
            title="Asignar Evaluación a Alumnos"
          >
            <UserPlus size={16} />
          </button>
        )}

        {docenteView && showActions && (
          <button
            onClick={() => {
              if (evaluacion.UnidadPlan?.planDeClasesId) {
                sessionStorage.setItem('lastViewedPlanId', evaluacion.UnidadPlan.planDeClasesId);
                sessionStorage.setItem('lastViewedUnidadId', evaluacion.UnidadPlan.id); // Guardar también la unidadId
              }
              navigate(`/docente/catedra/${catedraId}/evaluation/${evaluacion.id}`);
            }}
            className="p-2 bg-purple-600/20 text-purple-300 hover:bg-purple-600/30 hover:text-purple-200 rounded-lg transition-all duration-200 border border-purple-500/30 ml-2"
            title="Ver/Editar Evaluación"
          >
            <Eye size={16} />
          </button>
        )}
      </div>
    </div>
  );
};

export default EvaluationCard;